import React from 'react';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../context/ThemeContext';
import { View, Dimensions, Platform, StyleSheet } from 'react-native';
import { spacing } from '../constants/theme';

// Import hooks
import { useFriendRequests } from '../hooks/useFriendRequests';
import { useSharedRoutines } from '../hooks/useSharedRoutines';
import { useNotificationsListener } from '../hooks/useNotificationsListener';

// Import components
import Badge from '../components/Badge';

// Import screens
import ProfileSetupScreen from '../screens/profile/ProfileSetupScreen';
import ProfileScreen from '../screens/profile/ProfileScreen';
import FriendProfileScreen from '../screens/profile/FriendProfileScreen';
import WorkoutScreen from '../screens/workouts/WorkoutScreen';
import ExerciseDetailScreen from '../screens/workouts/ExerciseDetailScreen';
import RoutinesListScreen from '../screens/routines/RoutinesListScreen';
import CreateRoutineScreen from '../screens/routines/CreateRoutineScreen';
import EditRoutineScreen from '../screens/routines/EditRoutineScreen';
import JoinRoutineScreen from '../screens/routines/JoinRoutineScreen';
import ExerciseLibraryScreen from '../screens/exercises/ExerciseLibraryScreen';
import CreateExerciseScreen from '../screens/exercises/CreateExerciseScreen';
import ExerciseLibraryDetailScreen from '../screens/exercises/ExerciseLibraryDetailScreen';
import FriendManagementScreen from '../screens/social/FriendManagementScreen';
import RoutineSharingScreen from '../screens/social/RoutineSharingScreen';
import ProgressHistoryScreen from '../screens/workouts/ProgressHistoryScreen';
import WeightTrackingScreen from '../screens/workouts/WeightTrackingScreen';
import TermsConditionsScreen from '../screens/legal/TermsConditionsScreen';
import PrivacyPolicyScreen from '../screens/legal/PrivacyPolicyScreen';

// Define param lists for type safety
export type MainStackParamList = {
  MainTabs: {
    screen?: keyof MainTabParamList;
    initial?: boolean;
  } | undefined;
  ProfileSetup: undefined;
  FriendProfile: { friendId: string };
  ExerciseDetail: { exerciseId: string; routineId: string; dayOrder: number };
  EditRoutine: { routineId: string };
  CreateRoutine: undefined;
  JoinRoutine: undefined;
  CreateExercise: undefined;
  ExerciseLibraryDetail: { exerciseId: string };
  RoutineSharing: { routineId: string };
  WeightTracking: { exerciseId: string };
  FriendManagement: undefined;
  TermsConditions: undefined;
  PrivacyPolicy: undefined;
};

export type MainTabParamList = {
  Workout: undefined;
  Routines: undefined;
  Exercises: undefined;
  Progress: undefined;
  Profile: undefined;
};

const Stack = createNativeStackNavigator<MainStackParamList>();
const Tab = createBottomTabNavigator<MainTabParamList>();

// Agregar estilos para el MainNavigator
const styles = StyleSheet.create({
  iconContainer: {
    position: 'relative',
    width: 30,
    height: 30,
    justifyContent: 'center',
    alignItems: 'center',
  }
});

// Tab Navigator
const MainTabs = () => {
  const { theme } = useTheme();
  const { friendRequestsCount, sharedRoutinesCount } = useNotificationsListener();
  
  // Determinar si estamos en una pantalla pequeña
  const { width } = Dimensions.get('window');
  const isSmallScreen = width < 375; // iPhone SE o similar
  
  return (
    <Tab.Navigator
      screenOptions={({ route }) => ({
        tabBarIcon: ({ focused, color, size }) => {
          let iconName;

          if (route.name === 'Workout') {
            iconName = focused ? 'fitness' : 'fitness-outline';
          } else if (route.name === 'Routines') {
            iconName = focused ? 'list' : 'list-outline';
            
            // Mostrar badge si hay notificaciones de rutinas compartidas
            if (sharedRoutinesCount > 0) {
              return (
                <View style={styles.iconContainer}>
                  <Ionicons name={iconName as any} size={size} color={color} />
                  <Badge count={sharedRoutinesCount} size="small" color={theme.colors.primary} />
                </View>
              );
            }
          } else if (route.name === 'Exercises') {
            iconName = focused ? 'barbell' : 'barbell-outline';
          } else if (route.name === 'Progress') {
            iconName = focused ? 'trending-up' : 'trending-up-outline';
          } else if (route.name === 'Profile') {
            iconName = focused ? 'person' : 'person-outline';
            
            // Mostrar badge si hay notificaciones de solicitudes de amistad
            if (friendRequestsCount > 0) {
              return (
                <View style={styles.iconContainer}>
                  <Ionicons name={iconName as any} size={size} color={color} />
                  <Badge count={friendRequestsCount} size="small" color={theme.colors.error} />
                </View>
              );
            }
          }

          return <Ionicons name={iconName as any} size={size} color={color} />;
        },
        tabBarActiveTintColor: theme.colors.primary,
        tabBarInactiveTintColor: theme.colors.grey.medium,
        tabBarStyle: {
          backgroundColor: theme.colors.card,
          borderTopColor: theme.colors.border,
          height: isSmallScreen ? 50 : 56, // Altura reducida en pantallas pequeñas
          paddingTop: spacing.xs,
          paddingBottom: isSmallScreen ? spacing.xs : spacing.sm,
        },
        tabBarLabelStyle: {
          fontSize: 12,
          marginBottom: isSmallScreen ? 2 : 4,
        },
        // Ocultar etiquetas en pantallas pequeñas
        tabBarLabelPosition: isSmallScreen ? 'below-icon' : undefined,
        tabBarShowLabel: !isSmallScreen,
        headerStyle: {
          backgroundColor: theme.colors.card,
        },
        headerTintColor: theme.colors.text,
        headerShadowVisible: false,
      })}
    >
      <Tab.Screen name="Workout" component={WorkoutScreen} options={{ title: 'Entrenamiento' }} />
      <Tab.Screen name="Routines" component={RoutinesListScreen} options={{ title: 'Rutinas' }} />
      <Tab.Screen name="Exercises" component={ExerciseLibraryScreen} options={{ title: 'Ejercicios' }} />
      <Tab.Screen name="Progress" component={ProgressHistoryScreen} options={{ title: 'Progreso' }} />
      <Tab.Screen name="Profile" component={ProfileScreen} options={{ title: 'Perfil' }} />
    </Tab.Navigator>
  );
};

// Main Navigator with Tabs and Stack Screens
export const MainNavigator = () => {
  const { theme } = useTheme();
  
  return (
    <Stack.Navigator
      screenOptions={{
        headerShown: true,
        headerStyle: {
          backgroundColor: theme.colors.card,
        },
        headerTintColor: theme.colors.text,
        headerShadowVisible: false,
        contentStyle: {
          backgroundColor: theme.colors.background,
        },
      }}
      initialRouteName="MainTabs"
    >
      <Stack.Screen 
        name="MainTabs" 
        component={MainTabs} 
        options={{ headerShown: false }}
      />
      <Stack.Screen 
        name="ProfileSetup" 
        component={ProfileSetupScreen} 
        options={{ title: 'Configurar Perfil' }}
      />
      <Stack.Screen 
        name="FriendProfile" 
        component={FriendProfileScreen} 
        options={{ title: 'Perfil de Amigo' }}
      />
      <Stack.Screen 
        name="ExerciseDetail" 
        component={ExerciseDetailScreen} 
        options={{ title: 'Detalle de Ejercicio' }}
      />
      <Stack.Screen 
        name="EditRoutine" 
        component={EditRoutineScreen} 
        options={{ title: 'Editar Rutina' }}
      />
      <Stack.Screen 
        name="CreateRoutine" 
        component={CreateRoutineScreen} 
        options={{ title: 'Crear Rutina' }}
      />
      <Stack.Screen 
        name="JoinRoutine" 
        component={JoinRoutineScreen} 
        options={{ title: 'Unirse a Rutina' }}
      />
      <Stack.Screen 
        name="CreateExercise" 
        component={CreateExerciseScreen} 
        options={{ title: 'Crear Ejercicio' }}
      />
      <Stack.Screen 
        name="ExerciseLibraryDetail" 
        component={ExerciseLibraryDetailScreen} 
        options={{ title: 'Detalle de Ejercicio' }}
      />
      <Stack.Screen 
        name="RoutineSharing" 
        component={RoutineSharingScreen} 
        options={{ title: 'Compartir Rutina' }}
      />
      <Stack.Screen 
        name="WeightTracking" 
        component={WeightTrackingScreen} 
        options={{ title: 'Seguimiento de Peso' }}
      />
      <Stack.Screen 
        name="FriendManagement" 
        component={FriendManagementScreen} 
        options={{ title: 'Gestión de Amigos' }}
      />
      <Stack.Screen 
        name="TermsConditions" 
        component={TermsConditionsScreen} 
        options={{ title: 'Términos y Condiciones' }}
      />
      <Stack.Screen 
        name="PrivacyPolicy" 
        component={PrivacyPolicyScreen} 
        options={{ title: 'Política de Privacidad' }}
      />
    </Stack.Navigator>
  );
}; 